import { createDebug } from "./utils/LazyDebug";

import type * as LitNs from "lit";
import type * as WishlistAppNs from "./WishlistApp";
import type * as WishlistElementNs from "./WishlistElement";
import type * as WishlistElementHeadlessNs from "./WishlistElementHeadless";

const debug = createDebug("wk:loader");

window.headlessElements = new Map<
  string,
  new (host: HTMLElement) => WishlistElementHeadlessNs.WishlistElementHeadless
>();

export class AppLoader {
  #cache = new Map<string, Promise<WishlistModule | WishlistCoreModule>>();

  #sdkHost = "";
  #themeCodeHost = "";
  #themeCodeVersion = "";

  #eventTarget = new EventTarget();

  constructor({
    sdkHost,
    themeCodeHost,
    themeCodeVersion,
  }: {
    sdkHost: string;
    themeCodeHost: string;
    themeCodeVersion: string;
  }) {
    this.#sdkHost = sdkHost;
    this.#themeCodeHost = themeCodeHost;
    this.#themeCodeVersion = themeCodeVersion;
    this.#initDevMode();
  }

  getSdkUrl(file: string) {
    if (!file) return "";
    return `${this.#sdkHost}/${file}`;
  }

  getThemeCodeUrl(file: string) {
    if (!file) return "";
    return `${this.#themeCodeHost}/${file}?v=${this.#themeCodeVersion}`;
  }

  load(asset: WishlistAsset) {
    const { name, url, isDependency } = asset;
    const assetKey = name ?? url;

    if ((!name && !url) || !assetKey) {
      return Promise.resolve({} as WishlistModule);
    }

    if (!asset.type && asset.url) {
      asset = {
        ...asset,
        type: this.#getAssetTypeByUrl(asset.url),
      };
    }

    if (isDependency) {
      debug(`Load dependecy: ${assetKey}`, { asset });
    } else {
      debug(`Load: ${assetKey}`, { asset });
    }

    const cachedPromise = this.#cache.get(assetKey);
    if (cachedPromise) {
      return cachedPromise;
    }

    asset.dependencies?.forEach((asset) => (asset.isDependency = true));

    const dependencies: Promise<WishlistModule | WishlistCoreModule>[] =
      asset.dependencies?.map((dependency) => this.load(dependency)) ?? [];

    // With this, we cannot inject before element loaded
    if (asset.type === "headless-component") {
      dependencies.push(
        this.load({
          type: "core",
          name: "wishlist-element-headless",
          url: this.getSdkUrl("wishlist-element-headless.js"),
          isDependency: true,
        }),
      );
    } else if (asset.type === "lit-component") {
      dependencies.push(
        this.load({
          type: "core",
          name: "wishlist-element",
          url: this.getSdkUrl("wishlist-element.js"),
          isDependency: true,
        }),
      );
    }

    const loadPromise = Promise.all([
      this.#loadAsset(asset),
      ...dependencies,
    ]).then(async ([mainModule]) => {
      await asset.afterDependencies?.();

      if (asset.dependencies) {
        for (const dependecy of asset.dependencies) {
          await dependecy.afterMainModule?.();
        }
      }

      asset.afterLoad?.(mainModule);
      return mainModule;
    });

    this.#cache.set(assetKey, loadPromise);
    document.dispatchEvent(new CustomEvent(`wk:loader:${assetKey}:load`));

    return loadPromise;
  }

  async #loadAsset(asset: WishlistAsset) {
    const { url, type } = asset;
    if (!url) return Promise.resolve({} as WishlistModule);

    const internalUrl = this.#parseDependencyUrl({
      url,
      noCache: this.#isDevMode(),
    });

    const hasDependencies = !!asset.dependencies?.length;

    if (this.#isDevMode()) {
      debug(`Load dev asset: ${internalUrl}`, { asset });
    }

    switch (type) {
      case "css":
        return this.#loadCss(internalUrl, asset.order);
      case "js":
        return this.#loadJs(internalUrl).then((module) =>
          this.#processJs(asset, module),
        );
      case "json":
        return this.#loadJson(internalUrl).then((module) =>
          this.#processJson(module),
        );
      case "lit-component":
        return this.#loadJs(internalUrl).then((module) => {
          if (asset.isDependency) {
            debug(`Wait for main module: ${internalUrl}`, { asset });
            asset.afterMainModule = async () => {
              debug(`Main module loaded: ${internalUrl}`, { asset });
              await this.#processLitComponent(asset, module);
            };
            return Promise.resolve(module);
          } else if (hasDependencies) {
            debug(`Wait for dependencies: ${internalUrl}`, { asset });
            asset.afterDependencies = async () => {
              debug(`Dependencies loaded: ${internalUrl}`, { asset });
              await this.#processLitComponent(asset, module);
            };
            return Promise.resolve(module);
          } else {
            return this.#processLitComponent(asset, module);
          }
        });
      case "headless-component":
        return this.#loadJs(internalUrl).then((module) =>
          this.#processHeadlessComponent(asset, module),
        );
      case "core":
        return this.#loadCoreJs(internalUrl).then((module) =>
          this.#processCore(asset, module),
        );
      default:
        return Promise.resolve({} as WishlistModule);
    }
  }

  async #processJson(module: WishlistModule) {
    debug("Loaded json", { module });
    return module;
  }

  async #processCore(asset: WishlistAsset, module: WishlistCoreModule) {
    debug(`Loaded core module: ${asset.name}`, { asset, module });

    if ((module as WishlistAppModule).WishlistApp) {
      debug("Create wishlist app", { asset, module });

      window.WishlistKing = new (module as WishlistAppModule).WishlistApp(
        asset.props as WishlistAppNs.WishlistKingParams,
      );

      return {
        ...module,
        artifact: window.WishlistKing,
      };
    }

    return module;
  }

  async #processLitComponent(asset: WishlistAsset, module: WishlistModule) {
    const { setup, inject, define } = module;

    debug(`Loaded lit component: ${asset.name}`, { asset, module });

    const appModule = (await this.waitForCore(
      "wishlist-app",
    )) as WishlistAppModule;

    const app = appModule.artifact as WishlistAppNs.WishlistApp;

    if (!asset.isDependency) {
      if (typeof setup === "function") {
        debug(`Setup lit component: ${asset.name}`, { asset, module });
        setup({ app, props: asset.props });
      } else if (typeof inject === "function") {
        debug(`Inject lit component: ${asset.name}`, { asset, module });
        inject(app, asset.props);
      }
    }

    const wishlistElement = (await this.waitForCore(
      "wishlist-element",
    )) as WishlistElementModule;

    if (typeof define === "function") {
      const customElementName = asset.customElementName || asset.name;

      if (customElementName && window.customElements.get(customElementName))
        return module;

      const element = define(
        { ...(asset.props ?? {}), ...wishlistElement },
        asset.props,
      );
      debug(`Define lit component: ${customElementName}`, {
        asset,
        module,
        element,
        wishlistElement,
      });
      if (element && customElementName) {
        window.customElements.define(
          customElementName,
          element as WishlistElementClass,
        );
      }
    }

    return module;
  }

  async #processHeadlessComponent(
    asset: WishlistAsset,
    module: WishlistModule,
  ) {
    const { setup, inject, define } = module;

    debug(`Loaded headless component: ${asset.name}`, { asset, module });

    // Headless components need to be defined before they can be injected
    const wishlistElement = (await this.waitForCore(
      "wishlist-element-headless",
    )) as WishlistElementHeadlessModule;

    if (typeof define === "function") {
      const element = define(wishlistElement);
      debug(`Define headless component: ${asset.name}`, {
        asset,
        module,
        element,
      });
      if (element && asset.name) {
        window.headlessElements?.set(
          asset.name,
          element as WishlistElementHeadlessClass,
        );
      }
    }

    const appModule = (await this.waitForCore(
      "wishlist-app",
    )) as WishlistAppModule;
    const app = appModule.artifact as WishlistAppNs.WishlistApp;

    if (typeof setup === "function") {
      debug(`Setup headless component: ${asset.name}`, { asset, module });
      setup({ app });
    } else if (typeof inject === "function") {
      debug(`Inject headless component: ${asset.name}`, { asset, module });
      inject(app);
    }

    return module;
  }

  async #processJs(asset: WishlistAsset, module: WishlistModule) {
    const { setup } = module;

    debug(`Loaded js component: ${asset.name}`, { asset, module });

    // Continue without stalling the load process
    this.waitForCore("wishlist-app").then((coreModule) => {
      if (
        typeof setup === "function" &&
        (coreModule as WishlistAppModule).artifact
      ) {
        const app = (coreModule as WishlistAppModule)
          .artifact as WishlistAppNs.WishlistApp;
        debug(`Setup js component: ${asset.name}`, { asset, module });
        setup({ app });
        if (asset.name) {
          this.#eventTarget.dispatchEvent(
            new CustomEvent(`${asset.name}:setup`),
          );
        }
      }
    });

    return module;
  }

  async waitFor(key: string) {
    const cachedPromise = this.#cache.get(key);

    if (!cachedPromise) {
      console.warn("Waiting for unknwon module:", key);
      return Promise.resolve({} as WishlistModule);
    }

    return cachedPromise as Promise<WishlistModule>;
  }

  async waitForCore(key: string) {
    const cachedPromise = this.#cache.get(key);

    if (!cachedPromise) {
      debug(`Defer wait until core module available: ${key}`);
      return new Promise<WishlistCoreModule>((resolve) => {
        document.addEventListener(
          `wk:loader:${key}:load`,
          () => {
            debug(`Deferred wait can continue: ${key}`);
            resolve(this.#cache.get(key) as Promise<WishlistCoreModule>);
          },
          { once: true },
        );
      });
    }

    debug(`Wait for core module: ${key}`);
    return cachedPromise as Promise<WishlistCoreModule>;
  }

  async once(eventName: string) {
    return new Promise((resolve) => {
      this.#eventTarget.addEventListener(eventName, () => resolve(null), {
        once: true,
      });
    });
  }

  async #loadCss(url: string, order: number = 0) {
    return new Promise<WishlistModule>((resolve, reject) => {
      // TODO: test media="print" onload="this.media='all'"
      const link = document.createElement("link");
      link.rel = "stylesheet";
      link.href = url;
      link.dataset.loaderOrder = order.toString();

      link.onerror = reject;
      link.onload = () => {
        debug("Loaded stylesheet", { url });
        resolve({} as WishlistModule);
      };

      const linkWithHigherOrder = Array.from(
        document.head.querySelectorAll<HTMLLinkElement>(
          "link[data-loader-order]",
        ),
      ).find((link) => parseInt(link.dataset.loaderOrder ?? "0") > order);

      if (linkWithHigherOrder) {
        document.head.insertBefore(link, linkWithHigherOrder);
      } else {
        document.head.appendChild(link);
      }
    });
  }

  async #loadJs(url: string) {
    return import(url) as Promise<WishlistModule>;
  }

  async #loadCoreJs(url: string) {
    return import(url) as Promise<WishlistCoreModule>;
  }

  async #loadJson(url: string) {
    return fetch(url).then<WishlistModule>((response) => response.json());
  }

  #getAssetTypeByUrl(url: string) {
    const extension = url.split(".").pop()?.split(/\#|\?/)[0].toString();
    if (!extension || !["js", "css", "json"].includes(extension)) return;

    return extension as AssetType;
  }

  #parseDependencyUrl({ url, noCache }: { url: string; noCache?: boolean }) {
    if (noCache && !url.includes("/themecode-templates/")) {
      return url
        .replace("cdn.appmate.io", "storage.googleapis.com/appmate-cdn")
        .replace(/v=\d*/, `v=${Date.now()}`);
    }
    return url;
  }

  #isDevMode() {
    const isThemePreview = !!document.getElementById("preview-bar-iframe");
    if (!isThemePreview) return false;

    return !!window.localStorage.getItem("wk_dev_mode");
  }

  #initDevMode() {
    if (window.location.search.includes("wk_dev_mode=on")) {
      debug("Activate dev mode");
      window.localStorage.setItem("wk_dev_mode", Date.now().toString());
    } else if (window.location.search.includes("wk_dev_mode=off")) {
      debug("Deactivate dev mode");
      window.localStorage.removeItem("wk_dev_mode");
    }
    if (this.#isDevMode()) {
      debug("Dev mode active");
      window.addEventListener(
        "message",
        (event) => {
          if (event.data === "WK_THEME_RELOAD") {
            window.localStorage.setItem("wk_dev_mode", Date.now().toString());
            window.location.reload();
          }
        },
        false,
      );
    }
  }
}

export interface WishlistAsset {
  name?: string;
  customElementName?: string;
  url?: string;
  dependencies?: Array<WishlistAsset>;
  props?: AssetSettings;
  isDependency?: boolean;
  afterLoad?: (module: WishlistModule | WishlistCoreModule) => void;
  afterDependencies?: () => Promise<void>;
  afterMainModule?: () => Promise<void>;
  type?: AssetType;
  order?: number;
}

export type AssetType =
  | "css"
  | "js"
  | "json"
  | "core"
  | "lit-component"
  | "headless-component";

export type AssetSettings = Record<string, any>;

export interface WishlistModule extends Record<string, any> {
  setup?: SetupFn;
  afterSetup?: () => void;
  define?: DefineFn;
  inject?: InjectFn; // Deprecated: use setup instead
}

export type WishlistAppModule = typeof WishlistAppNs & { artifact?: any };

export type WishlistElementModule = typeof WishlistElementNs;

export type WishlistElementHeadlessModule = typeof WishlistElementHeadlessNs;

export type LitModule = typeof LitNs;

export type WishlistCoreModule =
  | WishlistAppModule
  | WishlistElementModule
  | WishlistElementHeadlessModule;

export type ProcessFn = (module: WishlistModule) => void | Promise<void>;

export type SetupFn = (params: {
  app: WishlistAppNs.WishlistApp;
  props?: AssetSettings;
}) => void;

export type InjectFn = (
  app: WishlistAppNs.WishlistApp,
  props?: AssetSettings,
) => void;

export type WishlistElementClass = new (
  host: HTMLElement,
) => WishlistElementNs.WishlistElement;

export type WishlistElementHeadlessClass = new (
  host: HTMLElement,
) => WishlistElementHeadlessNs.WishlistElementHeadless;

export type DefineFn = (
  params: WishlistElementModule | WishlistElementHeadlessModule,
  props?: AssetSettings,
) => WishlistElementClass | WishlistElementHeadlessClass;

export type DefineHeadlessFn = (
  params: WishlistElementHeadlessModule,
) => WishlistElementHeadlessNs.WishlistElementHeadless;

declare global {
  interface Window {
    headlessElements?: Map<
      string,
      new (
        host: HTMLElement,
      ) => WishlistElementHeadlessNs.WishlistElementHeadless
    >;
  }
}
